// src/components/HomeNoticeModal.tsx (통째 교체용 최종본)
import { useEffect, useMemo, useState } from "react";

const LOCAL_KEY = "kailospay.homeNotice.snoozedUntil";
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

// ✅ 팝업 페이지
const POPUP_URL = "/home-notice-popup.html";

// ✅ 토글/이미지 조회 API
const PUBLIC_API = "/api/public/home-popup";

type HomePopupResp = {
  ok?: boolean;
  enabled?: boolean;
  imageUrl?: string | null;     // camel
  image_url?: string | null;    // snake
  updated_at?: string | null;
  updatedAt?: string | null;
};

function pick<T = any>(obj: any, ...keys: string[]): T | undefined {
  for (const k of keys) {
    if (obj && Object.prototype.hasOwnProperty.call(obj, k)) return obj[k];
  }
  return undefined;
}

function isSnoozed(): boolean {
  try {
    const raw = window.localStorage.getItem(LOCAL_KEY);
    const until = raw ? Number(raw) : 0;
    return Number.isFinite(until) && until > Date.now();
  } catch {
    return false;
  }
}

export default function HomeNoticeModal() {
  const [open, setOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const isMobile = useMemo(() => {
    if (typeof window === "undefined") return false;
    const ua = window.navigator.userAgent || "";
    return /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    (async () => {
      // ✅ 오늘 하루 보지 않기
      if (isSnoozed()) return;

      // ✅ 서버에서 enabled/image 먼저 확인 (여기서 걸러야 “OFF인데도 팝업 뜸”이 사라짐)
      let cfg: HomePopupResp | null = null;
      try {
        const res = await fetch(PUBLIC_API, { cache: "no-store", credentials: "include" });
        if (res.ok) cfg = (await res.json().catch(() => null)) as HomePopupResp | null;
      } catch {
        cfg = null;
      }

      const enabled = !!pick<boolean>(cfg, "enabled");
      const img = (pick<string | null>(cfg, "image_url", "imageUrl") ?? null) || null;
      const updatedAt = (pick<string | null>(cfg, "updated_at", "updatedAt") ?? null) || null;

      // ✅ 핵심: OFF면 아무 것도 띄우지 않음
      if (!enabled) return;

      // ✅ ON인데 이미지가 없으면 사용자에게 보여줄 게 없으니 띄우지 않음(원하면 모달로 “이미지 없음” 안내로 바꿀 수 있음)
      if (!img) return;

      // ✅ 데스크탑이면 팝업 우선
      const ua = window.navigator.userAgent || "";
      const isMobileUa = /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
      const isDesktop = !isMobileUa;

      // 팝업에 img를 query로 전달(서버가 일시적으로 느려도 팝업에서 바로 그림 뜨게)
      const v = encodeURIComponent(String(updatedAt || Date.now()));
      const popupHref = `${POPUP_URL}?force=1&img=${encodeURIComponent(img)}&v=${v}`;

      if (isDesktop) {
        const w = 520;
        const h = 760;
        const left = window.screenX + Math.max(0, (window.outerWidth - w) / 2);
        const top = window.screenY + Math.max(0, (window.outerHeight - h) / 2);

        const features = [
          `width=${w}`,
          `height=${h}`,
          `left=${left}`,
          `top=${top}`,
          "menubar=no",
          "toolbar=no",
          "location=no",
          "status=no",
          "resizable=yes",
          "scrollbars=yes",
        ].join(",");

        const popup = window.open(popupHref, "kailospayNotice", features);
        if (popup) {
          try {
            popup.focus();
          } catch {}
          return; // ✅ 팝업 성공이면 모달은 안 띄움
        }
      }

      // ✅ 팝업이 막히면(모바일/브라우저 정책) 모달로 fallback
      setImageUrl(img);
      setOpen(true);
    })();
  }, []);

  useEffect(() => {
    if (!open || typeof document === "undefined") return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const close = (hideForADay: boolean) => {
    if (hideForADay && typeof window !== "undefined") {
      try {
        window.localStorage.setItem(LOCAL_KEY, String(Date.now() + ONE_DAY_MS));
      } catch {}
    }
    setOpen(false);
  };

  if (!open) return null;

  const MOBILE_FRAME_RATIO = "6 / 5";
  const MOBILE_OBJECT_POS = "50% 88%";
  const MODAL_MAX_W = isMobile ? "460px" : "520px";
  const MODAL_MAX_H = isMobile ? "90dvh" : "82dvh";

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-2">
        <div
          className="w-[92vw] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_12px_50px_rgba(15,23,42,0.45)]"
          style={{ maxWidth: MODAL_MAX_W, maxHeight: MODAL_MAX_H }}
        >
          <div className="grid h-full grid-rows-[auto,1fr,auto]">
            <div className="flex items-center justify-between bg-[#fff7ed] px-4 py-1 border-b border-[#fed7aa]">
              <span className="text-sm font-semibold text-[#c2410c]">중요 안내</span>
              <button
                type="button"
                onClick={() => close(false)}
                className="rounded-full px-2 py-0.5 text-slate-400 hover:text-[#c2410c]"
                aria-label="공지 창 닫기"
              >
                ✕
              </button>
            </div>

            <div className="min-h-0 px-4 py-2">
              <div className="flex h-full items-center justify-center">
                <div
                  className="w-full max-w-[380px] max-h-full overflow-hidden rounded-xl bg-white ring-1 ring-slate-200"
                  style={{ aspectRatio: isMobile ? MOBILE_FRAME_RATIO : "9 / 16" }}
                >
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt="공지 이미지"
                      draggable={false}
                      className="h-full w-full bg-white"
                      style={{
                        objectFit: isMobile ? "cover" : "contain",
                        objectPosition: isMobile ? MOBILE_OBJECT_POS : "50% 50%",
                      }}
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-xs text-slate-400">
                      공지 이미지를 불러오는 중입니다...
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="px-4 pb-4 pt-2 space-y-2">
              <button
                type="button"
                onClick={() => close(true)}
                className="w-full rounded-full border border-[#fed7aa] bg-white px-4 py-2 text-sm font-semibold text-[#c2410c] hover:bg-[#fff7ed]"
              >
                오늘 하루 이 창을 열지 않음
              </button>

              <button
                type="button"
                onClick={() => close(false)}
                className="w-full rounded-full bg-[#f97316] px-4 py-2 text-sm font-semibold text-white hover:bg-[#ea580c]"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
